import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, Network, Layers, Camera, Sparkles } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { useProviders, useActivateService } from '@/api/service-provisioning';
import { trackServiceActivate } from '@/lib/analytics-events';
import { PRODUCT_GROUP } from '@/lib/lead-product-group';
import { useSelfServeActivation } from '../activation-mode';
import { ServiceContactSalesDialog } from '../components/ServiceContactSalesDialog';
import { COMPUTE_PROVIDER, COMPUTE_PRODUCT, computeConsoleRoute } from './compute-activate';
import { OnboardingCss } from './onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from './onboarding-styles';

/**
 * Cloud Compute activation landing — the first-run screen shown when the org has
 * no Cloud Compute service. Mirrors WaapActivateLanding exactly: provider
 * resolution via useProviders (panda, status active), one-click activation via
 * useActivateService, then straight into the compute console (no onboarding
 * wizard — the customer creates their first instance from the dashboard).
 */
export function ComputeActivateLanding({ onBack }: { onBack?: () => void }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();
  const { data: providers = [], isLoading: providersLoading } = useProviders();
  const selfServeActivation = useSelfServeActivation();

  // Only an active `panda` provider can be provisioned self-serve.
  const panda = useMemo(
    () =>
      providers.find(
        (p) => p.name?.trim().toLowerCase() === COMPUTE_PROVIDER && p.status === 'active',
      ),
    [providers],
  );

  const activate = useActivateService();
  const email = user?.email ?? '';
  const unavailable = !providersLoading && !panda;
  const canActivate = !!panda && !!email && !activate.isPending;
  // Self-serve provisioning is gated (contact-sales for direct customers, on
  // while staff impersonate); when off the CTA opens the contact-sales dialog.
  const [contactOpen, setContactOpen] = useState(false);

  function handleActivate() {
    if (!panda || !canActivate) return;
    activate.mutate(
      { serviceName: COMPUTE_PRODUCT.serviceName, providerId: panda.id, email },
      {
        onSuccess: (service) => {
          trackServiceActivate(COMPUTE_PROVIDER, 'compute');
          show({ tone: 'success', title: 'Cloud Compute activated' });
          // No onboarding wizard — drop straight into the compute console where
          // the customer can create their first VM instance.
          navigate(computeConsoleRoute(service.id));
        },
        onError: (err) =>
          show({
            tone: 'danger',
            title: 'Activation failed',
            description: err.message || 'Could not activate Cloud Compute. Please try again.',
          }),
      },
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Server size={22} />}
        title="Cloud Compute"
        description="Virtual machines with networking, load balancers and backups on the Techev Cloud platform."
        actions={
          onBack ? (
            <Button variant="ghost" onClick={onBack}>
              Back to services
            </Button>
          ) : undefined
        }
      />

      {providersLoading ? (
        <Skeleton style={{ height: 200 }} />
      ) : unavailable ? (
        <Alert tone="warning" title={`${COMPUTE_PRODUCT.displayName} is not available yet`}>
          {COMPUTE_PRODUCT.displayName} is not available for self-serve activation on your account
          yet. Please contact our team to enable it.
        </Alert>
      ) : (
        <div className="vn-ob-hero" style={heroWrap}>
          <OnboardingCss />

          <span className="vn-ob-halo" style={heroIcon} aria-hidden>
            <Server size={30} />
          </span>

          <p style={heroEyebrow}>Cloud Compute</p>
          <h2 style={heroTitle}>Activate Cloud Compute for your organization</h2>
          <p style={heroLede}>
            Provision a compute environment and start creating virtual machines — a default zone is
            set up for you automatically, so you can launch your first VM right away.
          </p>

          <Button
            size="lg"
            loading={selfServeActivation && activate.isPending}
            startIcon={<Sparkles size={16} />}
            onClick={selfServeActivation ? handleActivate : () => setContactOpen(true)}
            disabled={selfServeActivation ? !canActivate : false}
            style={{ marginTop: 'var(--vn-space-2)' }}
          >
            {selfServeActivation && activate.isPending ? 'Activating…' : 'Activate Cloud Compute'}
          </Button>

          <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
            {PERKS.map((p) => (
              <div key={p.title} style={stepRow}>
                <span style={markerAccent}>{p.icon}</span>
                <div style={{ display: 'grid', gap: 2 }}>
                  <p style={stepTitle}>{p.title}</p>
                  <p style={stepDesc}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ServiceContactSalesDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        serviceLabel={COMPUTE_PRODUCT.displayName}
        productGroup={PRODUCT_GROUP.cloudService}
      />
    </Container>
  );
}

const PERKS = [
  {
    icon: <Server size={16} />,
    title: 'Virtual Machines',
    desc: 'Provision Linux and Windows VMs with configurable CPU, RAM and disk on Techev Cloud.',
  },
  {
    icon: <Network size={16} />,
    title: 'Networking',
    desc: 'Private networks, public IPs and floating IP management built in.',
  },
  {
    icon: <Layers size={16} />,
    title: 'Load Balancers',
    desc: 'Distribute traffic across your instances with L4/L7 load balancing.',
  },
  {
    icon: <Camera size={16} />,
    title: 'Snapshots',
    desc: 'Point-in-time snapshots of VM disks for backup and fast cloning.',
  },
];

export default ComputeActivateLanding;
